/**
 * The one Incident Required boundary for the shared workspace.
 *
 * It renders *instead of* the feature, never around it: an incident-bound
 * module that has already mounted will have run its store constructor, restored
 * whatever the previous incident left behind and issued its first incident-keyed
 * request long before any conditional rendering could hide it. Blocking the
 * mount is the only version of this gate that actually holds.
 *
 * Four states, because "no incident" is four different situations and an
 * operator has to be able to tell them apart: still loading, refused, nothing
 * running at all, or simply nothing picked yet.
 */
import type { ReactNode } from "react";
import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/empty-state";
import { workspaceText, type WorkspaceLocale } from "../i18n";

export interface WorkspaceIncidentAvailability {
  /** Discovery is still running; the answer below is not final yet. */
  readonly loading: boolean;
  /** Discovery failed or was refused. */
  readonly error: string | null;
  /** Whether the authorized projection holds any operationally active incident. */
  readonly hasActiveIncidents: boolean;
  readonly onRetry?: (() => void) | undefined;
}

/** Discovery refusals are a permission answer, not a transport failure. */
function isPermissionDenied(error: string): boolean {
  return error === "FORBIDDEN" || error === "INCIDENT_SCOPE_FORBIDDEN";
}

export function IncidentRequiredBoundary({
  availability,
  locale,
}: {
  readonly availability: WorkspaceIncidentAvailability | undefined;
  readonly locale: WorkspaceLocale;
}): ReactNode {
  if (availability?.loading === true) {
    return <EmptyState title={workspaceText(locale, "workspaceIncidentContextLoading")} />;
  }

  if (availability != null && availability.error !== null) {
    const denied = isPermissionDenied(availability.error);
    return (
      <EmptyState
        // Retry only where retrying can change the answer. A refusal needs a
        // different role or organization, not another attempt.
        action={denied || availability.onRetry === undefined ? undefined : (
          <Button onClick={availability.onRetry} variant="secondary">
            {workspaceText(locale, "workspaceIncidentContextRetry")}
          </Button>
        )}
        title={workspaceText(
          locale,
          denied ? "workspaceIncidentContextForbidden" : "workspaceIncidentContextError",
        )}
      />
    );
  }

  // Nothing is running: never fall back to a closed incident, the most recent
  // one, or the active organization — each of those silently invents a command
  // context the operator never chose.
  if (availability != null && !availability.hasActiveIncidents) {
    return (
      <EmptyState
        description={workspaceText(locale, "workspaceNoActiveIncidentsDescription")}
        title={workspaceText(locale, "workspaceNoActiveIncidents")}
      />
    );
  }

  return (
    <EmptyState
      description={workspaceText(locale, "workspaceIncidentRequiredDescription")}
      title={workspaceText(locale, "workspaceIncidentRequired")}
    />
  );
}
